import { AxiosError } from 'axios'
import { makeAutoObservable, reaction } from 'mobx'
import { createContext, useContext } from 'react'
import {
  GfxConditionType,
  GfxRecordsData,
  GfxSpecificCondition,
  GfxSvgRecord,
} from '../../api/gfx/interfaces'
import { State } from '../../api/vehicle/interfaces'
import { Optional } from '../../common-interfaces/generic-interfaces'
import { ErrorMessageBody } from '../../features/partsCatalog/Selections/components/CheckboxList'
import miscellaneousVehicle, {
  isMiscellaneousVehicle,
} from '../../features/search/Results/utils/miscellaneousVehicle'
import { getRandomVehicleId } from '../../helpers/utils'

import { StoreInstances } from '../../store/StoreInstancesContainer'

import { show as showNiceModal } from '@ebay/nice-modal-react'
import {
  cartSlice,
  checkAreAllCartsEmpty,
  getCartMode,
  isVehicleComplete,
  makeNewCartVehicle,
  productsMatch,
  SellerCartVehicle,
} from 'cart'
import { StorageKeys, Translate2 } from 'common'
import {
  IdValueGeneric,
  IdValuePair,
  NonCachedSearchType,
  ProductModel,
} from 'interfaces'
import {
  getSortOptions,
  SaytRecommendation,
  STORAGE_APPLIED_FILTERS_KEY,
  clearPersistedAppliedFilters,
} from 'results'
import { embeddedConfigManager } from '../../api/embedded/EmbeddedConfigManager.ts'
import { EmbeddedCommunicationsManager } from '../../api/embedded/EmbeddedCommunicationsManager'
import {
  CatalogIndexTracker,
  CatalogLookupType,
} from '../../api/metrics/CatalogIndexTracker'
import { PartsBody, PartsRequest } from '../../api/part/interface'
import { ServiceIntervalsResponse } from '../../api/searchResults/interfaces'
import { SearchType, TrackingManager } from '../../config/analytics/tracking'
import {
  buildVehicleStringDimension,
  GaTrackOption,
} from '../../config/analytics/tracking/utils'
import { Config, IS_INTEGRATED_VERSION } from '../../config/ConfigManager'
import { stockOptionsCases } from '../../features/filterResults/components/stockOptionsCases'
import { SelectDiagram } from '../../features/gfx/gfxSpecificConditions/DiagramSelector.ts'
import {
  IdsEnginesResponse,
  IPartItemResponse,
  PartTypeItem,
} from '../../features/partsCatalog/Selections/interfaces'
import { MultiPartInquiry } from '../../features/search/AllProductsSearch/interfaces'
import { ProductEngineSelector } from '../../features/search/AllProductsSearch/productEngineSelector'
import { VehicleWidgetDisplayState } from '../../features/search/VehicleSearch/store/VehicleWidgetStore'
import { getInterchangeParts } from '../../helpers/getInterchangeParts.ts'
import CatalogServiceProvider from '../../services/CatalogServiceProvider'
import { IStoreCard } from '../../services/MultiSellerServicesProvider.ts'
import PartServiceProvider from '../../services/PartServiceProvider'
import UserDataServiceProvider from '../../services/UserDataServiceProvider'
import { PartsCatalogSelections } from '../models/PartsCatalogModels'
import { PartType } from '../models/PartsCatalogStoreModels'
import { Region } from '../models/Regions'
import {
  AstSearchResultsRequest,
  AstSelection,
  Breadcrumbs,
  InterchangeBreadcrumbAction,
  Direction,
  Filters,
  InterchangeRequest,
  MileageOptions,
  OeParts,
  RequestParameterMileageOptions,
  RequestParameterRecommendedServices,
  SearchResultsModel,
  SearchResultsRequest,
  SearchResultsResponse,
  SortOrder,
  UpdatedPartInfoDetails,
} from '../../store/models/SearchModels'
import { parseBoolean } from '../../ui-components/util/CommonFunctions'
import {
  DiscardAndStartNewOrderActions,
  DuplicateVehicleActions,
  ToastNotificationVariant,
} from '../../ui-components/util/Interfaces'

import { CartMode } from '../models/ShoppingCartModels'
import { Vehicle } from '../models/Vehicles'
import { PartsCatalog, PartsCatalogType } from '../partsCatalog/PartsCatalog'
import { CatalogTab } from '../uiState/UiStateStore'
import { UserAttributeKey } from '../user/interfaces'
import { MiscPreferencesKeys } from '../user/UserStore'
import { CGPs, VehiclePreviousSearches } from '../vehicleHistory/interfaces'
import { store } from 'store'
import VehicleServiceProvider from 'src/services/VehicleServiceProvider.ts'
import { findFirstSimilarCartVehicle } from 'libs/cart/src/store/cartSelectors.ts'
import ShowNotification from 'src/ui-components/modals/ToastNotifcationNotiStack.tsx'
import { DEFAULT_SORT_ORDER } from 'libs/results/src/constants.ts'
import { hawkSearchTracking } from 'src/helpers/HawkSearchTracking.ts'
import { hawkSearchProvider } from 'src/config/analytics/core/providers/HawkSearchServiceProvider.ts'
import { prepareEventData } from 'src/helpers/HawkSearchEventHelpers.ts'

export const SELECTED_VEHICLE_KEY = 'selected-vehicle'

const LOAD_PER_REQUEST = 10

export const SearchStoreContext = createContext<SearchStore>(undefined)

const GFX_SKIP_SWITCH_LOOKUP_MODAL = 'gfx-skip-switch-lookup-modal'
const checkSkipModal = () =>
  sessionStorage.getItem(GFX_SKIP_SWITCH_LOOKUP_MODAL) === 'true'

export const STORAGE_SEARCH_STATE_KEY = 'search-state'

const partDomain = () =>
  StoreInstances?.userStore?.getUserAttribute(UserAttributeKey.partDomain)

const RETRY_SEARCH_LIMIT = 3

enum ClearSAYT {
  YES,
  NO,
}

export enum FilterGroupName {
  LINES = 'Lines',
  STOCK = 'Stock',
  BRANDS = 'Brands',
  PART_TYPE = 'Part Type',
}

export enum LineTypes {
  PRIMARY = 'Primary',
  SECONDARY = 'Secondary',
  ALL_LINES = 'All Lines',
}

export enum StockOptions {
  ONLY_PRICED = 'Only Priced',
  ONLY_STOCKED = 'Only Stocked',
  SHOW_ALL = 'Show All',
}

const showGfxError = (groupName: string): void => {
  StoreInstances.uiStore.openModalNotification({
    title: 'unavailableGroupGraphic',
    body: () => ErrorMessageBody(groupName),
    confirmText: 'yesIUnderstand',
    onConfirm: () => StoreInstances.uiStore.closeModalNotification(),
    onCancel: () => StoreInstances.uiStore.closeModalNotification(),
  })
}

export enum NewSearch {
  newSearch = 1,
  notNewSearch = 0,
}

export class SearchStore {
  currentVehicle: Vehicle = {}

  states: Array<State> = []

  statesLoading = false

  diagrams: SearchResultsModel = undefined

  searchResults: SearchResultsModel = { filters: [] }

  engineOptions = []

  filters: Filters = []

  defaultStockFilterValue = ''

  sortOrder: SortOrder = DEFAULT_SORT_ORDER

  gfxSvg: GfxSvgRecord

  gfxId: string = null

  gfxItem: IdValueGeneric<string, string> = { id: null, value: null }

  gfxData: GfxRecordsData

  gfxConditionsAnswers: Map<string, string>

  gfxAttributesAnswers: Map<string, string>

  gfxSelections: Array<string>

  gfxLastSelectedGroup: IdValueGeneric<string, string>

  gfxLookupData: string = undefined

  gfxLookupMade = false

  gfxIconIsLoading = false

  groupId: string = null

  isLoading = false

  loadingEngineOptions = false

  hasMoreResults = false

  lookupTitle: string = undefined

  oePartsLookupLoading = false

  oeParts: OeParts = []

  searchError = ''

  breadCrumbs: Breadcrumbs[] = []

  showPartsNotFitVehicle = false

  showGfxSwitchLookupModal = false

  showVehicleTransition = false

  lastNonCachedSearch: NonCachedSearchType = NonCachedSearchType.none

  lastSaytCategories: PartsCatalogSelections

  lastMultiPartInquiry: MultiPartInquiry[] = []

  lookupItems: PartsBody[] = []

  _selectedSeller?: IStoreCard

  /*
    Note:
      lastTextSearched
      lastInterchangeTextSearched
      We could have used a single variable but in the future we'll have to display a history
      of sayt searches, so the lastTextSearched will become a list
  */
  lastTextSearched: string

  lastInterchangeTextSearched: string

  partDetailsLoading = false

  currentFieldsSelections: Vehicle = {}

  partDetailsError = ''

  selectedServiceIntervalsType = null

  mileageOptions = []

  selectedMileageOptions = null

  recommendedServices: ServiceIntervalsResponse[] = []

  recommendedServicesLoading = false

  mileageOptionsLoading = false
  public cclVehiclesData: number[] = null

  selectedCatalogDiagram: ProductModel = {
    lineCode: '',
    partNumber: '',
    location: [],
  }

  showAddAlltoCart = false

  cartSelectorModal = false

  retrySearchCount = 0

  catlogIndexForSearchByNavigation: CatalogLookupType = null

  private _saytRecommendations: SaytRecommendation[]

  cgts: PartsCatalogSelections = []

  private _interchangeRequestData: InterchangeRequest = null

  public set saytRecommendations(recommendations: SaytRecommendation[]) {
    this._saytRecommendations = recommendations
  }

  public get saytRecommendations(): SaytRecommendation[] {
    return this._saytRecommendations
  }

  public set selectedSeller(seller: IStoreCard) {
    this._selectedSeller = seller
  }

  public get selectedSeller(): IStoreCard {
    return this._selectedSeller
  }

  updatedPartInfoDetails: Array<UpdatedPartInfoDetails> = []

  engineOptionsForProducts = []

  invalidInterchangePart = false

  inquiryTimeoutError = false

  constructor() {
    this.gfxAttributesAnswers = new Map<string, string>()
    this.gfxConditionsAnswers = new Map<string, string>()
    makeAutoObservable(this)
    this.restoreVehicleFromSession()
    this.loadBreadCrumbsFromStorage()
    this.currentFieldsSelections = this.currentVehicle
  }

  public setSelectedServiceIntervalsType = (
    selected: IdValueGeneric<string | number, string>
  ): void => {
    this.selectedServiceIntervalsType = selected
  }

  public setCclVehicleData = (data: number[]): void => {
    this.cclVehiclesData = data
  }

  public setMileageOptions = (
    selected: IdValueGeneric<string | number, string>[]
  ): void => {
    this.mileageOptions = selected
  }

  public setDefaultStockFilterValue = (value: string): void => {
    this.defaultStockFilterValue = value
  }

  public setSelectedMileageOptions = (
    selected: IdValueGeneric<string | number, string>
  ): void => {
    this.selectedMileageOptions = selected
  }

  public setRecommendedServices = (
    recommendedServices: ServiceIntervalsResponse[]
  ): void => {
    this.recommendedServices = recommendedServices
  }

  setShowVehicleTransition = (state: boolean): void => {
    this.showVehicleTransition = state
  }

  setGfxIconIsLoading = (loading: boolean): void => {
    this.gfxIconIsLoading = loading
  }

  public setCurrentFieldsSelections = (vehicle: Vehicle): void => {
    this.currentFieldsSelections = vehicle
  }

  handleAddBreadCrumb = (breadcrumb: Breadcrumbs): void => {
    if (this.breadCrumbs.length === 0) {
      this.addBreadCrumb(breadcrumb)
    } else {
      this.cleanBreadCrumbs()
      this.addBreadCrumb(breadcrumb)
    }
  }

  addBreadCrumb = (breadcrumb: Breadcrumbs): void => {
    this.breadCrumbs.push(breadcrumb)
    localStorage.setItem('breadCrumbs', JSON.stringify(this.breadCrumbs))
  }

  private readonly updateInterchangeBreadcrumb = (
    partNumber: string,
    action: InterchangeBreadcrumbAction
  ): void => {
    if (action === 'skip') {
      return
    }

    const newBreadCrumb: Breadcrumbs = {
      page: 'interchange',
      breadcumb: partNumber,
      url: () => getInterchangeParts(partNumber, 'skip'),
    }

    if (action === 'add') {
      this.addBreadCrumb(newBreadCrumb)
      return
    }

    const lastInterchangeIndex = this.breadCrumbs
      .map((crumb) => crumb.page)
      .lastIndexOf('interchange')

    if (lastInterchangeIndex >= 0) {
      this.breadCrumbs = [
        ...this.breadCrumbs.slice(0, lastInterchangeIndex),
        newBreadCrumb,
        ...this.breadCrumbs.slice(lastInterchangeIndex + 1),
      ]
      localStorage.setItem('breadCrumbs', JSON.stringify(this.breadCrumbs))
      return
    }

    this.addBreadCrumb(newBreadCrumb)
  }

  cleanBreadCrumbs = (): void => {
    this.breadCrumbs = []
  }

  updateBreadCrumbs = (breadcrumbs: Breadcrumbs[]): void => {
    this.breadCrumbs = breadcrumbs
  }

  removeBreadCrumbs = (): void => {
    this.breadCrumbs = []
    localStorage.removeItem('breadCrumbs')
  }

  private loadBreadCrumbsFromStorage = (): void => {
    const rawBreadCrumbs = localStorage.getItem('breadCrumbs')
    if (!rawBreadCrumbs) {
      return
    }

    this.cleanBreadCrumbs()

    let storedBreadCrumbs: Breadcrumbs[] = []
    try {
      const parsed = JSON.parse(rawBreadCrumbs)
      storedBreadCrumbs = Array.isArray(parsed) ? parsed : []
    } catch {
      storedBreadCrumbs = []
    }

    // `url` is a function so it doesn't survive JSON serialization. Drop any
    // stale/malformed entries (missing page or breadcumb) and restore a
    // no-op `url` so consumers can safely call `item.url?.()`.
    const validBreadCrumbs = storedBreadCrumbs
      .filter((crumb) => !!crumb?.page && !!crumb?.breadcumb)
      .map((crumb) => ({
        ...crumb,
        // eslint-disable-next-line @typescript-eslint/no-empty-function -- url is stripped by JSON serialization, restore as a safe no-op.
        url: crumb.url ?? (() => {}),
      }))

    if (validBreadCrumbs.length !== storedBreadCrumbs.length) {
      localStorage.removeItem('breadCrumbs')
    }

    this.updateBreadCrumbs(validBreadCrumbs)
  }

  updateCurrentVehicle = async (
    vehicle: Vehicle,
    preSelectedAction?: DuplicateVehicleActions
  ): Promise<boolean> => {
    if (!Config?.isNotCartOnlyMode) {
      return false
    }
    store.dispatch(cartSlice.actions.cleanupCart(this.currentVehicle)) // Should happen before addVehicleToCart
    const { shouldMergeCarts: askTheUser, shouldDiscardAndStartNewCart } =
      StoreInstances.uiStore
    const { START_NEW, MERGE, CANCEL } = DuplicateVehicleActions

    const isSingleCartMode = getCartMode() === CartMode.SINGLE_VEHICLE
    const vehicleFound = findFirstSimilarCartVehicle(store.getState(), vehicle)
    const isMiscellaneous = isMiscellaneousVehicle(vehicleFound?.vehicle)

    const merge =
      vehicleFound && (isSingleCartMode || isMiscellaneous) ? MERGE : undefined
    const startNew = !vehicleFound ? START_NEW : undefined

    const actionToTake =
      preSelectedAction ?? merge ?? startNew ?? (await askTheUser(vehicleFound))

    if (MERGE === actionToTake) {
      if (vehicle.engine.id !== miscellaneousVehicle.engine.id) {
        this.currentVehicle = {
          ...vehicleFound.vehicle,
          ...vehicle,
          specificConditions: [],
        }
        StoreInstances.specificConditions.clearAnswers()
      } else {
        this.currentVehicle = {}
      }
      return true
    }

    if (START_NEW === actionToTake) {
      const newVehicle: Vehicle = isMiscellaneousVehicle(vehicle)
        ? { ...vehicle }
        : { ...vehicle, id: getRandomVehicleId() }
      const shouldStartNewCart =
        isSingleCartMode &&
        (checkAreAllCartsEmpty(store.getState().cart.cartData) ||
          (await shouldDiscardAndStartNewCart()) ===
            DiscardAndStartNewOrderActions.START_NEW)

      if (shouldStartNewCart) {
        store.dispatch(
          cartSlice.actions.removeAllVehiclesFromCart(GaTrackOption.track)
        )
      }

      const isUpdatingCart = !isSingleCartMode || shouldStartNewCart
      if (isUpdatingCart) {
        try {
          if (
            isVehicleComplete(newVehicle) &&
            !isMiscellaneousVehicle(newVehicle)
          ) {
            // MPV3 8458 – add decoded vehicle to history
            const decodedVehicle =
              await VehicleServiceProvider.addVehicleToHistory(newVehicle)

            this.startNewCart({ ...decodedVehicle, id: getRandomVehicleId() })
            return true
          }
          this.startNewCart(newVehicle)
          return true
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error while updating cart with vehicle:', error)
          this.startNewCart(newVehicle)
          return true
        }
      }
      this.cancelUpdateCurrentVehicle()
    }
    if (CANCEL !== actionToTake) {
      this.cancelUpdateCurrentVehicle()
    }
    return false
  }

  private cancelUpdateCurrentVehicle = (): void => {
    StoreInstances.laborStore.lookup.resetStore()
    this.gfxConditionsAnswers = new Map<string, string>()
    this.gfxAttributesAnswers = new Map<string, string>()
    this.currentVehicle.specificConditions = []
  }

  private startNewCart = (vehicle: Vehicle) => {
    let newCartVehicle: SellerCartVehicle
    if (vehicle.engine.id !== miscellaneousVehicle.engine.id) {
      const id = vehicle?.id ? vehicle.id : getRandomVehicleId()
      this.currentVehicle = { ...vehicle, id }
      newCartVehicle = makeNewCartVehicle({
        ...vehicle,
        id,
      })
    } else {
      this.currentVehicle = {}
      newCartVehicle = makeNewCartVehicle({
        ...vehicle,
      })
    }
    this.setCurrentFieldsSelections({ ...vehicle })
    StoreInstances.vehicleWidget.setIsDrawerOpened(false)
    StoreInstances.vehicleWidget.setDisplayState(VehicleWidgetDisplayState.view)
    store.dispatch(
      cartSlice.actions.createCart(JSON.parse(JSON.stringify(newCartVehicle)))
    )
    // Clear empty vehicles.
    store.dispatch(cartSlice.actions.cleanupCart(newCartVehicle.vehicle))
  }

  deleteCurrentVehicle = (): void => {
    this.currentFieldsSelections = {}
    this.currentVehicle = {}
    store.dispatch(cartSlice.actions.cleanupCart(this.currentVehicle))
    sessionStorage.setItem(STORAGE_SEARCH_STATE_KEY, '') // delete also the search state
    this.searchResults = {}
    this.diagrams = undefined
  }

  updateJsonSelections = (item: VehiclePreviousSearches): void => {
    const { categories, groups, parts } = item

    sessionStorage.setItem(
      StorageKeys.SELECTED_PARTS_CATALOG_KEY,
      JSON.stringify({ categories, groups, parts })
    )
  }

  retrieveJSONSelections = (): CGPs => {
    const savedJSONCGPs =
      sessionStorage.getItem(StorageKeys.SELECTED_PARTS_CATALOG_KEY) || '{}'
    return JSON.parse(savedJSONCGPs)
  }

  getStatesWithCountryId = async (): Promise<void> => {
    this.statesLoading = true
    const states = await UserDataServiceProvider.getStatesByCountry(
      StoreInstances.userStore.country.countryId
    )
    this.states = states
    this.statesLoading = false
  }

  persistCurrentVehicle = (): void => {
    if (this.currentVehicle?.engine) {
      const v = this.currentVehicle
      const { countryCode } = StoreInstances.userStore.country
      const regionId = Region[countryCode]
      TrackingManager.setVehicle({
        year: v.year.value,
        make: v.make.value,
        model: v.model.value,
        engine: v.engine.value,
        id: buildVehicleStringDimension(v, regionId),
      })
    }
    sessionStorage.setItem(
      SELECTED_VEHICLE_KEY,
      JSON.stringify(this.currentVehicle)
    )
  }

  setGroupId = (groupId: string): void => {
    this.groupId = groupId
  }

  setGfxId = (id: string): void => {
    this.gfxId = id
  }

  setGfxItem = (item: IdValueGeneric<string, string>): void => {
    this.gfxItem = item
  }

  setLookupTitle = (title: string): void => {
    this.lookupTitle = title
  }

  setOePartsLookupLoading = (loading: boolean): void => {
    this.oePartsLookupLoading = loading
  }

  public buildBreadCrumbText = (
    items: string[],
    maxItemsForDisplay = 3
  ): string => {
    if (items.length > maxItemsForDisplay) {
      const displayedItems = items.slice(0, maxItemsForDisplay).join(' | ')
      return `${displayedItems} +${items.length - maxItemsForDisplay} ${Translate2('more')}`
    }

    return items.join(' | ')
  }

  public searchByLookupItems = (parts: PartsBody[]): void => {
    this._interchangeRequestData = null
    this.breadCrumbs = []

    const breadcrumbText = this.buildBreadCrumbText(
      parts.map((p) => p.partNumber)
    )
    const newBreadCrumb: Breadcrumbs = {
      page: 'listLookup',
      breadcumb: breadcrumbText,
      url: () => this.searchBySpecificParts(),
    }
    this.addBreadCrumb(newBreadCrumb)

    this.lookupItems = parts
    this.searchBySpecificParts()
  }

  public searchBySpecificParts = (start = 0): void => {
    CatalogIndexTracker.setIndex(CatalogLookupType.AST_NO_MATCH)
    this.setShowPartsNotFitVehicleLabel(true)
    this.clearGfxSvg()
    if (start === 0) {
      const previousPartsBinId = this.searchResults?.partsBinId
      this.resetFiltersForNewSearch()
      this.clearResults()
      this.clearPartsBinCache(previousPartsBinId)
    }
    const reqData = this.prepareBySpecificPartsRequestData(start)

    this.executeSearch(() => PartServiceProvider.byPartsApi(reqData), start > 0)
    this.lastNonCachedSearch = NonCachedSearchType.searchBySpecificParts
  }

  setSearchResults = (results: SearchResultsResponse): void => {
    const currentPartBin = this.searchResults?.partsBinId
    const updatedPartBin = !results.partsBinId
      ? currentPartBin
      : results.partsBinId

    const filters = results.filters || this.searchResults.filters

    this.searchResults = {
      ...this.searchResults,
      ...results,
      partsBinId: updatedPartBin,
      filters,
    }
  }

  setShowPartsNotFitVehicleLabel = (show: boolean): void => {
    this.showPartsNotFitVehicle = show
  }

  get showPartsNotFitVehicleLabel(): boolean {
    return this.showPartsNotFitVehicle
  }

  setFilters = (filters: Filters): void => {
    this.filters = filters
    if (filters[0]?.items[0]?.valueOf() !== LineTypes.ALL_LINES) {
      this.clearResults(ClearSAYT.NO)
    }
  }

  setSortOrder = (sortOrder: SortOrder): void => {
    this.sortOrder = sortOrder
  }

  setGfxData = (
    data: GfxRecordsData,
    selectedPartsGroup: IdValueGeneric<string, string>
  ): void => {
    this.gfxData = { ...data, selectedPartsGroup }
  }

  setShowGfxSwitchLookupModal(show: boolean): void {
    this.showGfxSwitchLookupModal = show
  }

  get gfxHasLoaded(): boolean {
    return this.gfxSvg.hasLoaded ?? false
  }

  setGfxHasLoaded = (hasLoaded: boolean): void => {
    this.gfxSvg.hasLoaded = hasLoaded
  }

  resetGfx = (): void => {
    if (this.gfxSvg) {
      this.gfxSvg.refreshKey = Math.random()
      this.setGfxHasLoaded(false)
    }

    this.gfxLookupMade = false
    this.gfxLookupData = undefined
    this.setLookupTitle(undefined)
    this.gfxLastSelectedGroup = undefined
    this.gfxSelections = undefined
  }

  clearGfxSvg = (): void => {
    if (!this.gfxSvg) {
      return
    }
    this.resetGfx()
    this.gfxSvg = undefined
  }

  forceClearGfx = async (): Promise<void> => {
    const selectedDiagram = SelectDiagram(this)
    try {
      const { selectedPartsGroup } = this.gfxData
      this.setGfxHasLoaded(false)
      this.clearGfxSvg()
      this.gfxSvg = await CatalogServiceProvider.getGFX({
        vehicle: this.currentVehicle,
        group: {
          id: selectedPartsGroup.id,
          value: selectedPartsGroup.value,
        },
        gfxAssetNbr: selectedDiagram,
        browserType: 1,
      })
      this.searchGfxByNavigation()
    } catch (error) {
      throw new Error(error.toString())
    }
  }

  changeEngine = (engineSelection: IdValuePair<string>): void => {
    this.currentVehicle.engine = {
      id: parseInt(engineSelection.id, 10),
      value: engineSelection.value,
    }
    StoreInstances.uiStore.setShowEnginesModal(false)
    this.openGFX()
  }

  resetFiltersForNewSearch = (): void => {
    this.setFilters([])
    clearPersistedAppliedFilters()
  }

  public answerGfxQuestion = (
    type: GfxConditionType,
    code: string,
    label: string
  ): void => {
    if (type === 'attribute') {
      this.gfxAttributesAnswers.set(code, label)
    } else if (type === 'condition') {
      this.gfxConditionsAnswers.set(code, label)
    }
  }

  public getGfxAnswer(type: GfxConditionType, code: string): Optional<string> {
    if (type === GfxConditionType.Attribute) {
      return this.gfxAttributesAnswers.get(code)
    }
    return this.gfxConditionsAnswers.get(code)
  }

  public openGFX = async (): Promise<void> => {
    try {
      this.setGfxIconIsLoading(true)
      this.setGfxId(this.gfxItem?.id)
      const gfxSpecCondResp =
        await CatalogServiceProvider.getGFXSpecificConditions({
          vehicle: this.currentVehicle,
          group: {
            id: parseInt(this.gfxItem.id, 10),
            value: this.gfxItem.value,
          },
        })

      if (gfxSpecCondResp === undefined) {
        StoreInstances.uiStore.displayErrorNotification(
          'GFX image is not found'
        )
        this.setGfxIconIsLoading(false)
        this.setGfxId(null)
        return
      }

      if (
        this.lastNonCachedSearch === NonCachedSearchType.searchByNavigation ||
        this.lastNonCachedSearch ===
          NonCachedSearchType.searchGfxByNavigation ||
        this.lastNonCachedSearch === NonCachedSearchType.none
      ) {
        /* Part types are a dependency for Specific Conditions,
           So the order below is really important */
        await StoreInstances.partsCatalog.selectSingleGroup(this.gfxItem.id)
        StoreInstances.partsCatalog.selectAllPartTypesInGroup(this.gfxItem.id)
      }
      this.setGfxData(gfxSpecCondResp, this.gfxItem)

      await StoreInstances.specificConditions.fetchSpecificConditionQuestions(
        this.currentVehicle,
        this.lastNonCachedSearch === NonCachedSearchType.searchByNavigation ||
          this.lastNonCachedSearch ===
            NonCachedSearchType.searchGfxByNavigation ||
          this.lastNonCachedSearch === NonCachedSearchType.none
          ? StoreInstances.partsCatalog.terminologies?.length > 0
            ? StoreInstances.partsCatalog.terminologies
            : StoreInstances.partsCatalog.searchedPartTypes
          : StoreInstances.specificConditions.terminologies,
        PartsCatalogType.PARTS_SEARCH,
        false /* error notification */
      )
      this.setGfxIconIsLoading(false)
      this.setGfxId(null)
      StoreInstances.uiStore.openGfxConditions()
    } catch (_error) {
      this.setGfxIconIsLoading(false)
      this.setGfxId(null)
      showGfxError(this.gfxItem.value)
    }
  }

  handleOnCancelGfxLookup = async (): Promise<void> => {
    const selectedDiagram = SelectDiagram(this)
    if (!this.gfxSelections && !this.gfxLastSelectedGroup) {
      this.resetGfx()
      return
    }
    const selectedPart = this.gfxSelections[0] /* From the last selection */
    const selectedGroup = this.gfxLastSelectedGroup
    this.setGfxHasLoaded(false)
    try {
      this.gfxSvg = await CatalogServiceProvider.getGFX({
        vehicle: this.currentVehicle,
        group: {
          id: selectedGroup.id,
          value: selectedGroup.value,
        },
        selectedGfxPartTypeIds: selectedPart,
        gfxAssetNbr: selectedDiagram,
        browserType: 1,
      })
      this.gfxSvg.refreshKey = Math.random()
    } catch (_e) {
      StoreInstances.uiStore.displayErrorNotification('Error refreshing GFX')
    }
  }

  handleOnConfirmGfxLookup = async (): Promise<void> => {
    const data = this.gfxLookupData
    this.showGfxSwitchLookupModal = false
    const currentData = data.split('darkred,')
    const groupAndDescDetails = currentData[1].split('|')[0]
    const groupAndDescArray = groupAndDescDetails.split('\u001D')
    const details = groupAndDescArray[0].split(',')
    const group = { id: details[1], value: details[1] }
    this.gfxLastSelectedGroup = group
    this.gfxSelections = details.slice(2, details.length)
    const selectedGfxPartTypeIds = details.slice(2, details.length)[0]
    const selectedDiagram = SelectDiagram(this)

    await StoreInstances.partsCatalog
      .fetchGfxTerminologies([group])
      .then(async (response) => {
        const partTypeItems = response as IPartItemResponse
        const cgts: PartsCatalogSelections = partTypeItems[0]?.items.map(
          (item: PartTypeItem) => {
            return {
              category: {
                id: 0,
                value: '',
              },
              group: {
                id: Number(group.id),
                value: group.value,
              },
              terminology: {
                id: Number(item.id),
                value: item.value,
              },
            }
          }
        )
        const reqData = this.prepareByNavigationRequestData(0, cgts)
        reqData.isGfxLookup =
          StoreInstances.userStore.getUserAttribute(
            UserAttributeKey.GFX_Enabled
          ) === 'true'
        await PartServiceProvider.getSearchResults(reqData).then((results) => {
          this.searchResults.partsBinId = results.partsBinId
        })
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.log(`cgts  --- ${JSON.stringify(e)}`)
      })

    try {
      this.setGfxHasLoaded(false)
      this.gfxSvg.refreshKey = Math.random()
      this.gfxSvg = await CatalogServiceProvider.getGFX({
        vehicle: this.currentVehicle,
        group: {
          id: group.id,
          value: group.value,
        },
        selectedGfxPartTypeIds,
        gfxAssetNbr: selectedDiagram,
        browserType: 1,
      })
    } catch (_e) {
      StoreInstances.uiStore.displayErrorNotification(
        'Error changing GFX group'
      )
    }
  }

  public handleGfxLookup = async (data: string): Promise<void> => {
    this.gfxLookupData = data
    if (SearchStore.isCurrentLookup(data)) {
      this.gfxLookupMade = true
      const currentData = data.split('darkgreen,')
      const groupAndDescDetails = currentData[1].split('|')[0]
      const groupAndDescArray = groupAndDescDetails.split('\u001D')
      const details = groupAndDescArray[0].split(',')
      this.gfxLastSelectedGroup = { id: details[1], value: details[1] }
      this.gfxSelections = details.slice(2, details.length)
      this.searchByGfxPartBin()
    } else if (SearchStore.isCurrentAllLinesLookup(data)) {
      ShowNotification({
        message: 'Current ALL Lines lookup',
        variant: ToastNotificationVariant.SUCCESS,
      })
    } else if (SearchStore.isNewGroupLookup(data)) {
      const skipModal = checkSkipModal()
      if (skipModal) {
        await this.handleOnConfirmGfxLookup()
      } else {
        this.showGfxSwitchLookupModal = true
      }
    } else if (SearchStore.isOELookup(data)) {
      const currentData = data.split('darkblue,')
      const groupAndDescDetails = currentData[1].split('|')[0]
      const groupAndDescArray = groupAndDescDetails.split('\u001D')
      const details = groupAndDescArray[0].split(',')
      const descriptionIds = details.slice(2, details.length)
      this.gfxSelections = descriptionIds
      this.getOeParts(details.slice(1, details.length))
    }
  }

  public get hasAnsweredAllGfxQuestions(): boolean {
    const answeredConditions = this.hasAnsweredAllGfx(
      GfxConditionType.Condition,
      this.gfxData?.specificConditions
    )

    const answeredAttributes = this.hasAnsweredAllGfx(
      GfxConditionType.Attribute,
      this.gfxData?.attributes
    )

    return answeredConditions && answeredAttributes
  }

  private hasAnsweredAllGfx(
    type: GfxConditionType,
    questions: Array<GfxSpecificCondition> | undefined
  ): boolean {
    if (!questions) {
      return true
    }
    for (const question of questions) {
      const answer = this.getGfxAnswer(type, question.code)
      if (answer === undefined) {
        return false
      }
    }
    return true
  }

  getOeParts = (newGroups: string[]): void => {
    this.setOePartsLookupLoading(true)
    CatalogServiceProvider.getGfxOeParts({
      vehicle: this.currentVehicle,
      partTypeIds:
        this.gfxSelections.length > 1
          ? this.gfxSelections.join(',')
          : `${this.gfxSelections[0]},`,
      group: {
        id: newGroups[0],
        value: newGroups[0],
      },
      specificConditions: this.currentVehicle?.specificConditions,
    })
      .then((response) => {
        this.oeParts = response
      })
      .catch(() => {
        StoreInstances.uiStore.displayErrorNotification(
          'Unable to retrieve OE part data'
        )
      })
      .finally(() => {
        this.setOePartsLookupLoading(false)
      })
  }

  searchByNavigation = (start = 0, cgts?: PartsCatalogSelections): void => {
    this.clearGfxSvg()
    if (start === 0) {
      const previousPartsBinId = this.searchResults?.partsBinId
      this.resetFiltersForNewSearch()
      this.clearResults()
      this.clearPartsBinCache(previousPartsBinId)
    }
    const reqData = this.prepareByNavigationRequestData(start, cgts)
    reqData.isGfxLookup = false
    this.executeSearch(
      () => PartServiceProvider.getSearchResults(reqData),
      start > 0
    )
    this.lastNonCachedSearch = NonCachedSearchType.searchByNavigation
  }

  searchByAstNavigation = (
    start = 0,
    resetFilters = true,
    filters?: Filters
  ): void => {
    this.setShowPartsNotFitVehicleLabel(true)
    this.clearGfxSvg()
    if (start === 0) {
      const previousPartsBinId = this.searchResults?.partsBinId
      if (resetFilters) {
        this.resetFiltersForNewSearch()
      }

      this.clearResults()
      this.clearPartsBinCache(previousPartsBinId)
    }
    const reqData = this.prepareAstPunchOutRequestData(
      start,
      resetFilters,
      filters
    )
    this.executeSearch(
      () => PartServiceProvider.getAstPunchOutSearchResults(reqData),
      start > 0,
      resetFilters
    )
    this.lastNonCachedSearch = NonCachedSearchType.searchByAstNavigation
  }

  searchGfxByNavigation = (start = 0): void => {
    if (start === 0) {
      const previousPartsBinId = this.searchResults?.partsBinId
      this.resetFiltersForNewSearch()
      this.clearResults()
      this.clearPartsBinCache(previousPartsBinId)
    }
    const reqData = this.prepareByNavigationRequestData(start)
    reqData.isGfxLookup =
      StoreInstances.userStore.getUserAttribute(
        UserAttributeKey.GFX_Enabled
      ) === 'true'

    if (
      reqData.cgts.length === 0 &&
      this.lastNonCachedSearch === NonCachedSearchType.searchBySaytCategories
    ) {
      reqData.cgts = this.cgts
    }

    if (
      reqData.cgts.length === 0 &&
      this.lastNonCachedSearch === NonCachedSearchType.searchByNavigation &&
      StoreInstances.partsCatalog.terminologies?.length === 0 &&
      StoreInstances.partsCatalog.searchedPartTypes?.length > 0
    ) {
      const partsCatalogSelections: PartsCatalogSelections =
        StoreInstances.partsCatalog.searchedPartTypes
          ?.filter((item) => item.isSelected)
          ?.map((item) => {
            return {
              category: {
                id: 999,
                value: '',
              },
              group: {
                id: item.groupId,
                value: item.groupValue,
              },
              terminology: {
                id: Number(item.id),
                value: item.value,
              },
            }
          })
      reqData.cgts = partsCatalogSelections
    }
    this.executeSearch(
      () => PartServiceProvider.getSearchResults(reqData),
      start > 0
    )
    this.lastNonCachedSearch = NonCachedSearchType.searchGfxByNavigation
  }

  private processSearchResults = async (
    results: SearchResultsResponse,
    append = false
  ): Promise<void> => {
    const { partsBinId } = this.searchResults
    const currentParts = this.searchResults?.parts ?? []

    const filters = results.filters || this.searchResults.filters

    const newParts = embeddedConfigManager.isLocalInventoryEnabled
      ? await EmbeddedCommunicationsManager.getLocalInventory(results)
      : results.parts || []

    if (!newParts?.length) {
      this.retrySearchCount++
      if (this.retrySearchCount >= RETRY_SEARCH_LIMIT) {
        this.setHasMoreResults(false)
        this.retrySearchCount = 0
      }
      return
    }

    const totalResults = results.filteredCount ?? results.totalCount ?? 0
    if (totalResults > 10) {
      this.setHasMoreResults(true)
    }

    this.setSearchResults({
      partsBinId,
      ...results,
      filters,
      parts: append ? [...currentParts, ...newParts] : [...newParts],
    })
  }

  // TODO: make this private again.
  public executeLastNonCachedSearch = (start: number): void => {
    switch (this.lastNonCachedSearch) {
      case NonCachedSearchType.searchByNavigation:
        this.searchByNavigation(start)
        break
      case NonCachedSearchType.searchGfxByNavigation:
        this.searchGfxByNavigation(start)
        break
      case NonCachedSearchType.searchByText:
        this.searchByText(this.lastTextSearched, start) // search term
        break
      case NonCachedSearchType.searchByInterchange:
        this.getInterchangeApiRequest(
          this._interchangeRequestData.interchangeText,
          this._interchangeRequestData.partTypes,
          this._interchangeRequestData.manufacturers,
          this._interchangeRequestData.partsBinId,
          parseBoolean(
            StoreInstances.userStore.miscPreferences?.[
              MiscPreferencesKeys.INTERCHANGE_PARTTYPES
            ]
          ),
          parseBoolean(
            StoreInstances.userStore.miscPreferences?.[
              MiscPreferencesKeys.INTERCHANGE_MANUFACTURERS
            ]
          ),
          'skip'
        )
        break
      case NonCachedSearchType.searchBySaytCategories:
        this.searchBySaytCategories(this.lastSaytCategories, start)
        break
      case NonCachedSearchType.searchByAstNavigation:
        this.searchByAstNavigation(start)
        break
      case NonCachedSearchType.searchBySpecificParts:
        this.searchBySpecificParts(start)
        break
      case NonCachedSearchType.searchByMultiPartsInquiry:
        this.searchMultiPartInquiry(this.lastMultiPartInquiry)
        break
      default: {
        // console.log('No supported search')
        break
      }
    }
  }

  searchByGfxPartBin = (startOffset?: number): void => {
    const start = startOffset ?? this.searchResults?.parts?.length ?? 0
    if (!this.searchResults || !this.searchResults.partsBinId) {
      return
    }
    this.clearResults()

    try {
      const reqData = this.prepareGfxPartsBinRequestData()
      this.executeSearch(
        () =>
          PartServiceProvider.getGfxPartsBinResults(
            this.searchResults.partsBinId,
            reqData
          ),
        start > 0
      )
    } catch (e) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
      if ((e as any)?.response?.status === 404)
        this.executeLastNonCachedSearch(start)
    }
  }

  searchByInterchange = (
    partNumber: string,
    start = 0,
    breadcrumbAction: InterchangeBreadcrumbAction = 'replace'
  ): void => {
    this.clearGfxSearchForSAYT()
    this.lastInterchangeTextSearched = partNumber
    CatalogIndexTracker.setIndex(CatalogLookupType.INTERCHANGE)
    if (start === 0) {
      const previousPartsBinId = this.searchResults?.partsBinId
      this.resetFiltersForNewSearch()
      this.clearResults()
      this.clearPartsBinCache(previousPartsBinId)
    }

    const reqData = this._interchangeRequestData

    const apiCall = async () => {
      const resp = await PartServiceProvider.getInterchangeResults(reqData)
      this.invalidInterchangePart = resp?.invalidPart ? resp.invalidPart : false
      return resp
    }

    this.executeSearch(apiCall, start > 0)

    if (start === 0) {
      this.updateInterchangeBreadcrumb(partNumber, breadcrumbAction)
    }

    this.lastNonCachedSearch = NonCachedSearchType.searchByInterchange
  }

  private getStockFilter = (): string => {
    const getItems = (g: string) =>
      this.filters?.filter((f) => f.groupName === g).flatMap((f) => f.items)
    const stock = getItems('Stock')?.[0]

    if (stock) return stock

    return this.getDefaultStockFilterOption(
      StoreInstances?.userStore?.preferences?.display_filtering,
      StoreInstances?.userStore?.preferences?.display_userFiltering
    )
  }

  clearGfxSearchForSAYT = (): void => {
    /* Clearing gfx search */
    this.gfxData = null
    this.gfxSvg = null
    if (this.gfxLookupMade) {
      this.resetGfx()
    }
  }

  searchByText = (text: string, start = 0): void => {
    this.clearGfxSearchForSAYT()
    /*
     * Reset catalog index to 0 (AST_NO_MATCH) for text search.
     * After an interchange search, the index is set to 4 (INTERCHANGE) which causes
     * a 400 error when sent as 'Catalog-Index' header to the text search API (catalogId=7)
     */
    CatalogIndexTracker.setIndex(CatalogLookupType.AST_NO_MATCH)
    if (start === 0) {
      const previousPartsBinId = this.searchResults?.partsBinId
      this.resetFiltersForNewSearch()
      this.clearResults()
      this.clearPartsBinCache(previousPartsBinId)
      /*
       * Reset sort order to default since text search API (catalogId=7) does not support
       * sort properties like 'Part Description' that may have been set during interchange search
       */
      this.sortOrder = DEFAULT_SORT_ORDER
    }
    const reqDataBase = this.prepareCommonRequestData(start)
    const reqData = {
      ...reqDataBase,
      userContext: {
        visitorId: hawkSearchTracking.getVisitorId(),
        visitId: hawkSearchTracking.getVisitorId(),
        queryId: hawkSearchTracking.replaceQueryIdCookie(),
        'user-agent': '',
      },
    }
    this.executeSearch(
      () =>
        PartServiceProvider.getSearchResultsByText(reqData, text, {
          catalogId: '7',
          covListId: partDomain(),
          partDomain: partDomain(),
        }),
      start > 0,
      true,
      text
    )
    TrackingManager.trackEvent('search', {
      search_type: SearchType.Text,
      search_term: text,
    })

    this.lastTextSearched = text
    this.lastNonCachedSearch = NonCachedSearchType.searchByText
  }

  searchBySaytCategories = (
    categories: PartsCatalogSelections,
    start = 0,
    searchText?: string
  ): void => {
    this.cgts = categories
    this.clearGfxSearchForSAYT()
    if (start === 0) {
      const previousPartsBinId = this.searchResults?.partsBinId
      this.resetFiltersForNewSearch()
      this.clearResults()
      this.clearPartsBinCache(previousPartsBinId)
      if (searchText) {
        this.lastTextSearched = searchText
      }
    }
    const common = this.prepareCommonRequestData(start)
    const req = {
      ...common,
      cgts: categories,
      vehicle: {
        ...this.currentVehicle,
        plate:
          this.currentVehicle.state !== undefined &&
          this.currentVehicle.state !== null
            ? `${this.currentVehicle.state} ${this.currentVehicle.plate}`
            : this.currentVehicle.plate === null
              ? ''
              : this.currentVehicle.plate,
      },
      isGfxLookup: false,
    }
    this.executeSearch(
      () => PartServiceProvider.getSearchResults(req),
      start > 0
    )
    this.lastSaytCategories = categories
    this.lastNonCachedSearch = NonCachedSearchType.searchBySaytCategories
  }

  setInquiryTimeoutError = (value: boolean): void => {
    this.inquiryTimeoutError = value
  }

  private isTimeoutError = (error: unknown): boolean => {
    if (error instanceof AxiosError) {
      return (
        error?.message?.includes('timeout') ||
        error.code === 'ECONNABORTED' ||
        error.code === 'ERR_NETWORK'
      )
    }
    return false
  }

  private executeSearch = async (
    apiCall: () => Promise<SearchResultsResponse>,
    appendResults: boolean,
    resetFilters?: boolean,
    text?: string
  ): Promise<void> => {
    this.setIsLoading(true)
    this.setHasMoreResults(true)
    this.setInquiryTimeoutError(false)
    StoreInstances.uiStore.showApplySortMessage = false
    const retryApiCall = async (
      attempt: number
    ): Promise<SearchResultsResponse> => {
      try {
        return await apiCall()
      } catch (error) {
        if (attempt < 2 && this.isTimeoutError(error)) {
          StoreInstances.uiStore.displayErrorNotification(
            `itsTakingLongerThanUsualToPerformYourInquiry`
          )
          return retryApiCall(attempt + 1)
        }
        throw error
      }
    }

    return retryApiCall(1)
      .then((results) => {
        if (!results?.partsBinId && Array.isArray(results)) {
          this.engineOptionsForProducts =
            results as unknown as IdsEnginesResponse
          showNiceModal(ProductEngineSelector)
          return undefined
        }
        if (results?.parts?.length === 0 && results?.filters?.length > 0) {
          this.searchResults.partsBinId = results.partsBinId
          this.searchResults.filters = results.filters
        }

        if (
          this.searchResults?.filters?.length > 0 &&
          (this.lastNonCachedSearch ===
            NonCachedSearchType.searchByAstNavigation ||
            this.lastNonCachedSearch === NonCachedSearchType.searchByText) &&
          !resetFilters
        ) {
          results.filters = this.searchResults.filters
        }

        if (!appendResults) {
          //resetting qtyUpdatedPartDetails to initial value.
          this.updatedPartInfoDetails = []
        }

        // when status code returns 204 and results are 0, resetting results and filters
        if (results.totalCount === undefined) {
          this.searchResults = { filters: [] }
          this.setIsLoading(false) // MPV3-5810 - temporary code - Nov 13th 2024
        }

        if (text) {
          hawkSearchProvider.trackEvent(
            'Search',
            prepareEventData('Search', {
              Qs: text,
              isNewSearch: NewSearch.newSearch,
              trackingID: results.astTrackingId,
            })
          )
        }

        return this.processSearchResults(results, appendResults)
      })
      .catch((e) => {
        this.searchResults = {}
        this.setSearchError(e.message)
        this.setInquiryTimeoutError(this.isTimeoutError(e))
      })
      .finally(() => {
        this.setIsLoading(false)
      })
  }

  public setIsLoading = (isLoading: boolean): void => {
    this.isLoading = isLoading
  }

  public setHasMoreResults = (hasMore: boolean): void => {
    this.hasMoreResults = hasMore
  }

  private setSearchError = (searchError: string) => {
    this.searchError = searchError
  }

  public clearResults = (clearSAYT: ClearSAYT = ClearSAYT.YES): void => {
    if (!this.searchResults) return

    this.searchResults.parts = []
    this.searchResults.totalCount = 0
    this.searchResults.filteredCount = 0
    this.searchResults.isSnwFinished = false
    this._selectedSeller = null
    if (clearSAYT === ClearSAYT.YES) {
      this.saytRecommendations = []
    }
  }

  private clearPartsBinCache = async (partsBinId: string): Promise<void> => {
    if (!partsBinId) return

    try {
      this.searchResults.partsBinId = undefined
      await PartServiceProvider.clearPartsBinCache(partsBinId)
    } catch (_e) {
      //
    }
  }

  getEngineOptions = async (): Promise<void> => {
    try {
      this.loadingEngineOptions = true
      const engines = await CatalogServiceProvider.getEngineOptionsPlain(
        this.currentVehicle
      )
      this.engineOptions = engines
    } catch (_error) {
      // console.log(error)
    } finally {
      this.loadingEngineOptions = false
    }
  }

  private prepareByNavigationRequestData = (
    start: number,
    cgts?: PartsCatalogSelections
  ): SearchResultsRequest => {
    const formattedVehicle = {
      ...this.currentVehicle,
      initialized: undefined,
    }
    formattedVehicle.plate =
      formattedVehicle.state && formattedVehicle.plate
        ? `${formattedVehicle.state} ${formattedVehicle.plate}`
        : null
    delete formattedVehicle.state
    const commonData = this.prepareCommonRequestData(start)
    return {
      ...commonData,
      vehicle: formattedVehicle,
      cgts: cgts ? cgts : SearchStore.partsCatalogSelections(),
      isJobsLookup:
        StoreInstances.uiStore.selectedCatalogTab === CatalogTab.JOBS_CATALOG
          ? true
          : false,
    }
  }

  private prepareBySpecificPartsRequestData = (start: number): PartsRequest => {
    this.setIsLoading(true)

    const commonData = this.prepareCommonRequestData(start)
    return {
      ...commonData,
      parts: this.lookupItems,
    }
  }

  private prepareAstPunchOutRequestData = (
    start: number,
    resetFilters?: boolean,
    filters?: Filters
  ): AstSearchResultsRequest => {
    const commonData = this.prepareCommonRequestData(start, filters)

    const addedUserContext = {
      ...commonData,
      userContext: {
        visitorId: hawkSearchTracking.getVisitorId(),
        visitId: hawkSearchTracking.getVisitorId(),
        queryId: resetFilters
          ? hawkSearchTracking.replaceQueryIdCookie()
          : hawkSearchTracking.fetchOrGenerateQueryIdViaCookie(),
        'user-agent': '',
      },
    }

    return {
      ...addedUserContext,
      astBrandTerminologyRequestItem:
        SearchStore.astPunchOutCatalogSelections(),
    }
  }

  private static astPunchOutCatalogSelections = (): AstSelection => {
    const { selectedPartTypes } = StoreInstances.astPunchOutStore.catalog
    const terminologies = selectedPartTypes?.map((sPT) => sPT.id)
    return { brands: [], terminologies } // We currently don't use the brand filter
  }

  private static partsCatalogSelections = (): PartsCatalogSelections => {
    const selectedSearch = StoreInstances.uiStore.selectedCatalogTab
    if (selectedSearch === CatalogTab.JOBS_CATALOG) {
      return StoreInstances.jobsCatalog.catalog.getCtgs()
    }
    return StoreInstances.partsCatalog.getCtgs()
  }

  private prepareGfxPartsBinRequestData = () => {
    const start = this.searchResults?.parts?.length ?? 0
    return {
      partsBinId: this.searchResults.partsBinId,
      gfxPartTypes: this.gfxSelections,
      ...this.prepareCommonRequestData(start),
    }
  }

  // TODO: refactor this function to make it easier to read.
  public getDefaultStockFilterOption = (
    displayFiltering: string,
    displayUserFiltering: string
  ): string => {
    const displayUserFilteringValue = (displayUserFilteringOption: string) => {
      if (displayUserFilteringOption === '1') {
        return StockOptions.ONLY_PRICED
      }

      if (displayUserFilteringOption === '2') {
        return StockOptions.ONLY_STOCKED
      }
      return StockOptions.SHOW_ALL
    }

    const stockOptions = stockOptionsCases(displayFiltering)

    const findDefaultOption = stockOptions.items.find(
      (item) => item === displayUserFilteringValue(displayUserFiltering)
    )
    if (findDefaultOption) {
      this.setDefaultStockFilterValue(
        displayUserFilteringValue(displayUserFiltering)
      )
      return displayUserFilteringValue(displayUserFiltering)
    }
    this.setDefaultStockFilterValue(stockOptions.items[0])
    return stockOptions?.items[0]
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public prepareCommonRequestData = (
    start: number,
    filtersOverride?: Filters
  ) => {
    const activeFilters = filtersOverride ?? this.filters
    const linesfilter = activeFilters.find(
      (filter) => filter.groupName === FilterGroupName.LINES
    )
    const selectedLine = linesfilter && linesfilter.items[0]

    let lineLookupType = 1
    if (selectedLine === LineTypes.SECONDARY) {
      lineLookupType = 2
    }

    const defaultStockFilter = activeFilters.find(
      (item) => item.groupName === FilterGroupName.STOCK
    )
      ? []
      : [
          {
            groupName: FilterGroupName.STOCK,
            items: [
              this.getDefaultStockFilterOption(
                StoreInstances?.userStore?.preferences?.display_filtering,
                StoreInstances?.userStore?.preferences?.display_userFiltering
              ),
            ],
          },
        ]
    return {
      start,
      limit: LOAD_PER_REQUEST,
      filters: [
        ...activeFilters.filter(
          // Remove the lines group from the filters.
          (filter) => filter.groupName !== FilterGroupName.LINES
        ),
        ...defaultStockFilter,
      ],
      lineLookupType,
      manufacturers: [],
      sortOrder: this.sortOrder.map((item) => ({
        property: item.property,
        dir: item.dir,
      })),
      clientUTCOffset: StoreInstances.userStore.utcOffset,
    }
  }

  private restoreVehicleFromSession = () => {
    if (!IS_INTEGRATED_VERSION) {
      const savedVehicleJson =
        sessionStorage.getItem(SELECTED_VEHICLE_KEY) ?? '{}'
      this.currentVehicle = JSON.parse(savedVehicleJson)
    }
  }

  public restoreFiltersFromCache = (): void => {
    const sortOrderString = StoreInstances?.userStore?.getUserAttribute(
      UserAttributeKey.displaySortOrder
    )
    if (sortOrderString) {
      const sortOrderNestedList = sortOrderString.split(',').map((option) => {
        const innerOption = option.split('_')
        return [innerOption[0].trim(), innerOption[1].trim()]
      })
      const options = getSortOptions()
      const sortByThenBy: SortOrder = sortOrderNestedList
        .slice(0, 2)
        .map((list) => {
          let dir: Direction = list[1] === '0' ? 'asc' : 'desc'
          let property = list[0]
          if (property === 'MCL') {
            property = 'Recommended'
            dir = 'desc' // Because Recommended has no asc dir in the options above
          } else if (property === 'Availability') {
            dir = 'desc' // Because Availability has no asc dir in the options above
          } else if (property === 'PartNumber') {
            property = 'Part Number'
          } else if (property === 'Description') {
            property = 'Part Description'
          }
          const label = options.find(
            (option) => option.property === property && option.dir === dir
          ).label
          return { property, dir, label }
        })
      if (sortByThenBy.length > 0) this.setSortOrder(sortByThenBy)
    } else {
      this.setSortOrder(DEFAULT_SORT_ORDER)
    }
  }

  static isNewGroupLookup = (data: string): boolean =>
    data.indexOf('darkred') >= 0

  static isCurrentLookup = (data: string): boolean =>
    data.indexOf('darkgreen') >= 0

  static isCurrentAllLinesLookup = (data: string): boolean =>
    data.indexOf('darkorange') >= 0

  static isOELookup = (data: string): boolean => data.indexOf('darkblue') >= 0

  public saveSearchState = (): void => {
    sessionStorage.setItem(STORAGE_SEARCH_STATE_KEY, JSON.stringify(this))
  }

  public restoreSearchState = (): void => {
    try {
      this.restoreFiltersFromCache()
    } catch (_e) {
      // Do nothing.
    }
    const savedStateStr = sessionStorage.getItem(STORAGE_SEARCH_STATE_KEY)
    if (savedStateStr) {
      const restoredState: SearchStore = JSON.parse(savedStateStr)
      this.lastNonCachedSearch = restoredState.lastNonCachedSearch
      this.lastSaytCategories = restoredState.lastSaytCategories
      this.lastMultiPartInquiry = restoredState.lastMultiPartInquiry
      this.lastTextSearched = restoredState.lastTextSearched
      this.lastInterchangeTextSearched =
        restoredState.lastInterchangeTextSearched
      this._interchangeRequestData = restoredState._interchangeRequestData
      this.gfxSvg = restoredState.gfxSvg
      if (this.gfxSvg) {
        this.gfxSvg.hasLoaded = false
      }
      this.gfxData = restoredState.gfxData
      this.gfxLookupData = restoredState.gfxLookupData
      this.gfxSelections = restoredState.gfxSelections
      this.gfxAttributesAnswers = new Map(restoredState.gfxAttributesAnswers)
      this.gfxConditionsAnswers = new Map(restoredState.gfxConditionsAnswers)
      this.currentVehicle = restoredState.currentVehicle

      const restoredCrumbs = Array.isArray(restoredState.breadCrumbs)
        ? restoredState.breadCrumbs
            .filter((crumb) => !!crumb?.page && !!crumb?.breadcumb)
            .map((crumb) => ({
              ...crumb,
              // eslint-disable-next-line @typescript-eslint/no-empty-function -- url is stripped by JSON serialization, restore as a safe no-op.
              url: crumb.url ?? (() => {}),
            }))
        : []

      if (restoredCrumbs.length > 0) {
        this.updateBreadCrumbs(restoredCrumbs)
      }

      // Re-running the search clears applied filters; keep a copy so Results can
      // restore them after the new parts bin is created.
      const savedAppliedFilters = sessionStorage.getItem(
        STORAGE_APPLIED_FILTERS_KEY
      )

      if (window.location.pathname === '/searchResults') {
        this.executeLastNonCachedSearch(0)
        if (savedAppliedFilters) {
          sessionStorage.setItem(
            STORAGE_APPLIED_FILTERS_KEY,
            savedAppliedFilters
          )
        }
      }
    }
  }

  public initReactions = async (): Promise<void> => {
    await StoreInstances.userStore.loadingUser
    this.restoreSearchState()
    /** Save any time the parts-bin updates */
    reaction(
      () => StoreInstances.searchStore.searchResults.partsBinId,
      () => {
        this.saveSearchState()
      }
    )
    reaction(
      () => this.currentVehicle,
      () => this.persistCurrentVehicle()
    )
    reaction(
      () => this.lastNonCachedSearch,
      () => this.handleShowAddAllToCart()
    )

    /** Save any time lastNonCachedSearch updates */
    reaction(
      () => this.lastNonCachedSearch,
      () => {
        this.saveSearchState()
      }
    )

    /** Save any time breadCrumbs update (e.g. interchange search on the same page) */
    reaction(
      () => this.breadCrumbs.slice(),
      () => {
        this.saveSearchState()
      }
    )
  }

  public gaLastSearchTerm = (): string => {
    if (
      this.lastNonCachedSearch === NonCachedSearchType.searchByText ||
      this.lastNonCachedSearch === NonCachedSearchType.searchBySaytCategories
    )
      return this.lastTextSearched

    if (this.lastNonCachedSearch === NonCachedSearchType.searchByInterchange)
      return this.lastInterchangeTextSearched

    return ''
  }

  setpartDetailsLoading = (loading: boolean): void => {
    this.partDetailsLoading = loading
  }

  setPartDetailsError = (error: string): void => {
    this.partDetailsError = error
  }

  setSelectedCatalogDiagram = (selectedCatalogDiagram: ProductModel): void => {
    this.selectedCatalogDiagram = selectedCatalogDiagram
  }

  /* TODO: implement proper hash */
  // Vehicles in cartVehicle have vehicleID but vehicles in OrderResponseDetail don't have vehicleID.
  // Hence we use orderIndex when we generate getVehicleHash from orderConfirmation Page.
  // We use vehcile.id when we generate getVehicleHash from cartPage.
  getVehicleHash = (vehicle: Vehicle, orderIndex?: number): string => {
    return (
      vehicle.year?.value +
      vehicle.make?.value +
      vehicle.model?.value +
      vehicle.subModel?.value +
      (vehicle.id ? vehicle.id : orderIndex)
    )
  }

  getMileageOptions = async (
    requestBody: Vehicle
  ): Promise<MileageOptions[]> => {
    this.mileageOptionsLoading = true
    const requestParameter: RequestParameterMileageOptions = {
      intervalType: 0,
      severeCode: !this.selectedServiceIntervalsType
        ? 'Normal'
        : this.selectedServiceIntervalsType.value,
    }
    const resp = await CatalogServiceProvider.getServiceIntervals(
      requestParameter,
      requestBody
    )
    this.setMileageOptions(
      resp?.map((item) => ({
        id: item.mileageNum,
        value: item.mileageText,
      }))
    )
    this.mileageOptionsLoading = false
    return resp
  }

  getRecommendedServicesTable = async (
    requestBody: Vehicle
  ): Promise<ServiceIntervalsResponse[]> => {
    this.recommendedServicesLoading = true
    const requestParameter: RequestParameterRecommendedServices = {
      intervalType: this.selectedServiceIntervalsType
        ? Number(this.selectedServiceIntervalsType.id) - 1
        : 0,
      severeCode: !this.selectedServiceIntervalsType
        ? 'Normal'
        : this.selectedServiceIntervalsType.value,
      mileage: this.selectedMileageOptions?.id,
    }
    const resp = await CatalogServiceProvider.getRecommendedServices(
      requestParameter,
      requestBody
    )
    this.setRecommendedServices(resp)
    this.recommendedServicesLoading = false
    return resp
  }

  public searchMultiPartInquiry = (
    multiPartsList: MultiPartInquiry[]
  ): void => {
    CatalogIndexTracker.setIndex(CatalogLookupType.AST_NO_MATCH)
    this.setShowPartsNotFitVehicleLabel(true)
    this.clearGfxSvg()
    const previousPartsBinId = this.searchResults?.partsBinId
    this.resetFiltersForNewSearch()
    this.clearResults()
    this.clearPartsBinCache(previousPartsBinId)

    const multiPartInquiry = multiPartsList.map((item) => {
      return {
        partNumber: item.partNumber.toString(),
        lineCode: item.lineCode,
        qty: item.stockQty,
        description: item.comment,
      }
    })

    const commonData = this.prepareCommonRequestData(0)
    const reqData = {
      ...commonData,
      parts: multiPartInquiry,
    }
    this.executeSearch(
      () => PartServiceProvider.byPartsApi(reqData, true),
      false
    )
    this.lastMultiPartInquiry = multiPartsList
    this.lastNonCachedSearch = NonCachedSearchType.searchByMultiPartsInquiry
    this.setShowAddAlltoCart(true)
  }

  setShowAddAlltoCart = (value: boolean): void => {
    this.showAddAlltoCart = value
  }

  public findSelectedPartTypeById(partTypeId: string): PartType {
    return this.lastPartsCatalogInstance().findSelectedPartTypeById(partTypeId)
  }

  public lastPartsCatalogInstance(): PartsCatalog {
    if (this.lastNonCachedSearch === NonCachedSearchType.searchByAstNavigation)
      return StoreInstances.astPunchOutStore.catalog
    return StoreInstances.partsCatalog
  }

  public closeCartSelectorModal = (): void => {
    this.cartSelectorModal = false
  }

  public setCartSelectorModal = (value: boolean): void => {
    this.cartSelectorModal = value
  }

  handleShowAddAllToCart = (): void => {
    const shouldShow =
      this.lastNonCachedSearch === NonCachedSearchType.searchByMultiPartsInquiry
    this.setShowAddAlltoCart(shouldShow)
  }

  /* This method should be only used only when we select CGPs and do a byNavigation kind of search. Not for interchange and All products search */
  public updateCatalogIndex = (): void => {
    if (this.currentVehicle.plate) {
      CatalogIndexTracker.setIndex(CatalogLookupType.PLATE)
    } else if (this.currentVehicle.vin) {
      CatalogIndexTracker.setIndex(CatalogLookupType.VIN)
    } else {
      // Lookup type is YMME.
      // Using AST_NO_MATCH as it has the same value as YMME.
      CatalogIndexTracker.setIndex(CatalogLookupType.AST_NO_MATCH)
    }
  }

  public setUpdatedPartInfoDetails = (
    updatedDetails: UpdatedPartInfoDetails
  ): void => {
    const hasQtyUpdatedPart = this.updatedPartInfoDetails.findIndex((item) =>
      productsMatch(updatedDetails.product, item.product)
    )
    if (hasQtyUpdatedPart > -1) {
      this.updatedPartInfoDetails[hasQtyUpdatedPart] = updatedDetails
    } else {
      this.updatedPartInfoDetails.push(updatedDetails)
    }
  }

  public getUpdatedPartInfoDetails = (
    product: ProductModel
  ): UpdatedPartInfoDetails => {
    return this.updatedPartInfoDetails?.find((item) =>
      productsMatch(product, item.product)
    )
  }

  public getInterchangeRequestData = (): InterchangeRequest => {
    return this._interchangeRequestData
  }

  public getInterchangeApiRequest = (
    interchangeText: string,
    partTypes: string[],
    manufacturers: string[],
    partsBinId: string,
    isPartTypeSkiped: boolean,
    isManufacturerSkiped: boolean,
    breadcrumbAction: InterchangeBreadcrumbAction = 'replace'
  ): unknown => {
    if (
      parseBoolean(
        StoreInstances.userStore.miscPreferences?.[
          MiscPreferencesKeys.INTERCHANGE_PARTTYPES
        ]
      ) !== isPartTypeSkiped ||
      parseBoolean(
        StoreInstances.userStore.miscPreferences?.[
          MiscPreferencesKeys.INTERCHANGE_MANUFACTURERS
        ]
      ) !== isManufacturerSkiped
    ) {
      StoreInstances.userStore.updateMiscPreferences({
        [MiscPreferencesKeys.INTERCHANGE_PARTTYPES]: `${isPartTypeSkiped}`,
        [MiscPreferencesKeys.INTERCHANGE_MANUFACTURERS]: `${isManufacturerSkiped}`,
      })
    }

    /*
      Due to an API limitation, the interchange search (Non-cached search only)
      doesn't support sorting by Cost, Availability or List
    */
    /*
    MPV3-5810 - temporary disabled code - Nov 13th 2024
    const notSupported = (i) =>
      i.label?.includes('Cost') ||
      i.label?.includes('Availability') ||
      i.label?.includes('List')

    const applySort = this.sortOrder?.filter(notSupported)?.length <= 0

    const sortOrder = applySort ? this.sortOrder : null */

    const sortOrder = this.sortOrder
    const stock = this.getStockFilter()

    const ApiRequestData = {
      interchangeText,
      partTypes,
      manufacturers,
      partsBinId,
      sortOrder,
      stock,
    }

    this._interchangeRequestData = ApiRequestData

    this.searchByInterchange(interchangeText, 0, breadcrumbAction)

    return ApiRequestData
  }
}

export const useSearchStore = (): SearchStore => {
  const searchStore = useContext(SearchStoreContext)
  if (!searchStore) {
    throw new Error(
      'No SearchStoreContext.Provider found when calling useSearchStore.'
    )
  }
  return searchStore
}
